import { useEffect, useState } from "react";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  tier: string;
  threshold: number | null;
}

interface EarnedAchievement {
  achievement_id: string;
  earned_at: string;
}

export function useAchievements() {
  const { user, gameStats } = useAuth();
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [earnedAchievements, setEarnedAchievements] = useState<EarnedAchievement[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAchievements = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        // Fetch all achievement definitions
        const { data: allAchievements, error: achievementsError } = await supabase
          .from("achievements")
          .select("*")
          .order("threshold", { ascending: true, nullsFirst: false });

        if (achievementsError) throw achievementsError;

        // Fetch user's earned achievements
        const { data: userAchievements, error: userAchievementsError } = await supabase
          .from("user_achievements")
          .select("achievement_id, earned_at")
          .eq("user_id", user.id);

        if (userAchievementsError) throw userAchievementsError;

        setAchievements(allAchievements || []);
        setEarnedAchievements(userAchievements || []);
      } catch (error) {
        console.error("Error fetching achievements:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAchievements();
  }, [user]);

  // Calculate progress for unearned achievements
  const progress: Record<string, number> = {};
  
  if (gameStats) {
    // Map achievement IDs to current progress
    progress.streak_3 = gameStats.current_streak || 0;
    progress.streak_7 = gameStats.current_streak || 0;
    progress.streak_14 = gameStats.current_streak || 0;
    progress.streak_30 = gameStats.current_streak || 0;
    progress.wins_10 = gameStats.total_won || 0;
    progress.wins_25 = gameStats.total_won || 0;
    progress.wins_50 = gameStats.total_won || 0;
    progress.wins_100 = gameStats.total_won || 0;
    progress.played_100 = gameStats.total_played || 0;
    progress.legend = gameStats.max_streak || 0;
  }

  return {
    achievements,
    earnedAchievements,
    isLoading,
    progress,
  };
}
